/* ═══════════════════════════════════════════════════════════════════════════
   v8.4 — RAID orchestration + Assumptions log + surfaced Dependencies + RACI/RAM.
   The Risk & Issue register (above) is section 1 of the RAID pane; this block adds
   sections 2–3 (assumptions, dependencies) and the separate RACI matrix tab.
   ═══════════════════════════════════════════════════════════════════════════ */
function renderRaid() {
  renderRisks();
  renderAssumptions();
  renderDependencies();
}

/* ── Assumptions ─────────────────────────────────────────────────────────── */
function _assumptionRegId(a) { return 'A-' + String(_mintDisplayOrd(ASSUMPTIONS, a)).padStart(3, '0'); }   /* v14 (W3b): derived */
function renderAssumptions() {
  const host = document.getElementById('assumptionList');
  const empty = document.getElementById('assumptionEmpty');
  if (!host) return;
  host.innerHTML = '';
  if (empty) empty.style.display = ASSUMPTIONS.length ? 'none' : '';
  ASSUMPTIONS.forEach(a => host.appendChild(_buildAssumptionCard(a)));
  const cEl = document.getElementById('raidAssumptionsCount');
  if (cEl) cEl.textContent = ASSUMPTIONS.length ? String(ASSUMPTIONS.length) : '';
}
function _buildAssumptionCard(a) {
  const card = document.createElement('div');
  card.className = 'assumption-card val-' + String(a.validation || 'Unvalidated').toLowerCase();
  card.dataset.id = a.id;
  const top = document.createElement('div');
  top.className = 'as-top';
  top.innerHTML =
    `<span class="as-regid" title="Assumption id">${esc(_assumptionRegId(a))}</span>` +
    `<span class="as-spacer"></span>` +
    `<label class="as-field">Confidence <select class="as-conf">${ASSUMPTION_CONFIDENCE.map(c => `<option value="${esc(c)}"${c === a.confidence ? ' selected' : ''}>${esc(c)}</option>`).join('')}</select></label>` +
    `<label class="as-field">Validation <select class="as-val">${ASSUMPTION_VALIDATION.map(v => `<option value="${esc(v)}"${v === a.validation ? ' selected' : ''}>${esc(v)}</option>`).join('')}</select></label>` +
    `<span class="rc-card-actions"><button class="rc-delete" title="Delete assumption">×</button></span>`;
  top.querySelector('.as-conf').addEventListener('change', (e) => { if (ASSUMPTION_CONFIDENCE.includes(e.target.value)) { a.confidence = e.target.value; markDirty(); } });
  top.querySelector('.as-val').addEventListener('change', (e) => { if (ASSUMPTION_VALIDATION.includes(e.target.value)) { a.validation = e.target.value; markDirty(); renderAssumptions(); } });
  top.querySelector('.rc-delete').addEventListener('click', () => deleteAssumption(a.id));
  card.appendChild(top);
  const stmt = document.createElement('div');
  stmt.className = 'rc-statement';
  stmt.contentEditable = 'true'; stmt.spellcheck = false;
  stmt.textContent = a.statement || '';
  stmt.setAttribute('data-placeholder', 'State the assumption…');
  _onBlurCommit(stmt, () => { const v = (stmt.textContent || '').trim(); if (v !== (a.statement || '')) { a.statement = v; markDirty(); } });
  card.appendChild(stmt);
  const meta = document.createElement('div');
  meta.className = 'rc-meta';
  const ownerField = document.createElement('span'); ownerField.className = 'rc-meta-field';
  ownerField.innerHTML = `Owner: <input type="text" list="stakeholderList" placeholder="(none)" value="${esc(a.owner || '')}">`;
  ownerField.querySelector('input').addEventListener('change', (e) => { const v = (e.target.value || '').trim(); if (v !== (a.owner || '')) { a.owner = v; markDirty(); } });
  meta.appendChild(ownerField);
  card.appendChild(meta);
  return card;
}
function addAssumption() {
  const a = { id: _nextAssumptionSeq(), statement: '', owner: '', confidence: 'Medium', validation: 'Unvalidated' };
  ASSUMPTIONS.push(a);
  markDirty();
  renderAssumptions();
  const card = document.querySelector(`#assumptionList .assumption-card[data-id="${a.id}"]`);
  if (card) { const st = card.querySelector('.rc-statement'); if (st) { card.scrollIntoView({ block: 'center' }); st.focus(); } }
}
function deleteAssumption(id) {
  const idx = ASSUMPTIONS.findIndex(a => a.id === id);
  if (idx < 0) return;
  const a = ASSUMPTIONS[idx];
  const label = (a.statement && a.statement.trim()) ? '"' + _escapeHtml(a.statement.trim()) + '"' : 'this assumption';
  showConfirm('Delete assumption?',
    'This will remove ' + label + ' (' + _escapeHtml(_assumptionRegId(a)) + ') from the log.<br><br>You\'ll have 6 seconds to undo.',
    () => {
      const i2 = ASSUMPTIONS.findIndex(x => x.id === id);
      if (i2 < 0) return;
      const removed = ASSUMPTIONS.splice(i2, 1)[0]; const removedIndex = i2;
      markDirty(); renderAssumptions();
      showUndoToast('Assumption deleted', () => {
        ASSUMPTIONS.splice(removedIndex, 0, removed);
        markDirty(); renderAssumptions();
        showToast('Assumption restored', { type: 'info' });
      });
    });
}

/* ── Dependencies (surfaced read-only from the action layer) ─────────────── */
function renderDependencies() {
  const host = document.getElementById('dependencyList');
  const empty = document.getElementById('dependencyEmpty');
  if (!host) return;
  host.innerHTML = '';
  /* v13.1 (A19): getAllActions(), not ACTIONS — the latter holds only what has been SAVED,
     so an action added this session and given a blocker was simply absent from the RAID
     Dependencies list, with no hint that anything was missing. Deleted-this-session actions
     are excluded for the same reason they are everywhere else: the array still holds them
     until Save. */
  const withDeps = getAllActions().filter(a => {
    if (deletedIds && deletedIds.has(a.id)) return false;
    const e = edits[a.id];
    return e && Array.isArray(e.dependsOn) && e.dependsOn.length;
  });
  if (empty) empty.style.display = withDeps.length ? 'none' : '';
  withDeps.forEach(a => {
    const e = edits[a.id];
    const item = document.createElement('div');
    item.className = 'dependency-item';
    const deps = e.dependsOn.map(id => `<span class="dep-chip">${esc(chipLabel(id))}</span>`).join(' ');
    item.innerHTML = `<div class="dep-head">${esc(chipLabel(a.id))}</div>` +
      `<div class="dep-sub"><span class="dep-arrow">depends on</span> ${deps}</div>`;
    host.appendChild(item);
  });
  const cEl = document.getElementById('raidDependenciesCount');
  if (cEl) cEl.textContent = withDeps.length ? String(withDeps.length) : '';
}

/* ── RACI / RAM matrix ───────────────────────────────────────────────────── */
const RACI_CYCLE = { '': 'R', 'R': 'A', 'A': 'C', 'C': 'I', 'I': '' };
function _raciGet(ws, stkId) {
  const a = (ws.assignments || []).find(x => x.stakeholderId === stkId);
  return a ? a.raci : '';
}
function _raciSet(ws, stkId, code) {
  ws.assignments = Array.isArray(ws.assignments) ? ws.assignments : [];
  /* Exactly one Accountable per workstream: setting a new A demotes the previous A to R. */
  if (code === 'A') ws.assignments.forEach(x => { if (x.raci === 'A' && x.stakeholderId !== stkId) x.raci = 'R'; });
  const existing = ws.assignments.find(x => x.stakeholderId === stkId);
  if (!code) ws.assignments = ws.assignments.filter(x => x.stakeholderId !== stkId);
  else if (existing) existing.raci = code;
  else ws.assignments.push({ stakeholderId: stkId, raci: code });
  markDirty();
}
function renderRaci() {
  const wrap = document.getElementById('raciMatrixWrap');
  const emptyWs = document.getElementById('raciEmptyWs');
  const emptyStk = document.getElementById('raciEmptyStk');
  if (!wrap) return;
  /* v8.7 (Q-c): matrix rows stay LEVEL-1 workstreams only — deeper WBS elements
     inherit their stream's RACI; the matrix stays compact and one-Accountable-per-
     stream stays meaningful. */
  const wss = _wsTreeIndex().roots, stks = STAKEHOLDERS;
  if (emptyWs) emptyWs.style.display = wss.length ? 'none' : '';
  if (emptyStk) emptyStk.style.display = (wss.length && !stks.length) ? '' : 'none';
  if (!wss.length || !stks.length) { wrap.innerHTML = ''; return; }

  const tbl = document.createElement('table');
  tbl.className = 'raci-table';
  const thead = document.createElement('thead');
  const hr = document.createElement('tr');
  const corner = document.createElement('th'); corner.className = 'raci-corner'; corner.innerHTML = 'Workstream <span class="raci-corner-slash">╲</span> Stakeholder';
  hr.appendChild(corner);
  stks.forEach(s => {
    const th = document.createElement('th'); th.className = 'raci-colh';
    th.innerHTML = `<span class="raci-colh-name">${esc(s.name || '(unnamed)')}</span>`;
    hr.appendChild(th);
  });
  thead.appendChild(hr); tbl.appendChild(thead);
  const tbody = document.createElement('tbody');
  wss.forEach(ws => {
    const tr = document.createElement('tr');
    const rh = document.createElement('th'); rh.className = 'raci-rowh';
    rh.textContent = ws.title || '(untitled workstream)';
    const hasA = (ws.assignments || []).some(x => x.raci === 'A');
    if (!hasA) { rh.classList.add('raci-rowh-noa'); rh.title = 'No one is Accountable for this workstream yet'; }
    tr.appendChild(rh);
    stks.forEach(s => {
      const td = document.createElement('td');
      const code = _raciGet(ws, s.id);
      td.className = 'raci-cell' + (code ? ' raci-' + code : '');
      td.textContent = code;
      td.title = 'Click to cycle R → A → C → I → blank';
      td.addEventListener('click', () => { _raciSet(ws, s.id, RACI_CYCLE[code] || ''); renderRaci(); });
      tr.appendChild(td);
    });
    tbody.appendChild(tr);
  });
  tbl.appendChild(tbody);
  wrap.innerHTML = '';
  wrap.appendChild(tbl);
}

/* ── v8.6: Communications plan (People · third section) ─────────────────────
   comm × audience · when · channel · owner. Data: appMeta.communications, ids from
   the self-healing high-water mark appMeta.comSeq (the crSeq pattern) — BOTH created
   lazily on first add, so files without comms round-trip byte-exact (no data change).
   Render paths are strictly READ-ONLY on appMeta (no ensure-at-render), because the
   boot render runs before the round-trip is checked. Audiences are stakeholder
   REFERENCES (ids) plus an "All stakeholders" flag (scoping decision Q-a, 2026-07-21);
   a deleted stakeholder simply drops off at render — dangling ids are inert. */
const COMM_FREQUENCIES = ['Daily', 'Weekly', 'Fortnightly', 'Monthly', 'At gates', 'Ad hoc'];   /* v8.6.1: + Daily (most frequent first) */
const COMM_CHANNELS = ['Email', 'Phone', 'Teams', 'Meeting', 'Report / deck', 'Other'];   /* v8.6.1: + Phone (beside Email, the direct 1:1 channels) */
function _commRegId(c) { return 'COM-' + String(_mintDisplayOrd(_commsRO(), c)).padStart(3, '0'); }   /* v14 (W3b): derived */
function _commsRO() { return Array.isArray(appMeta.communications) ? appMeta.communications : []; }
function _nextCommSeq() {
  const n = _mintNextId(_commsRO(), _myWriterId(), appMeta.comSeq);   /* v14 (W3b) */
  appMeta.comSeq = n;
  return n;
}
function _normComm(c) {
  if (typeof c.what !== 'string') c.what = '';
  if (typeof c.audienceAll !== 'boolean') c.audienceAll = false;
  if (!Array.isArray(c.audienceIds)) c.audienceIds = [];
  if (!COMM_FREQUENCIES.includes(c.frequency)) c.frequency = 'Ad hoc';
  if (!COMM_CHANNELS.includes(c.channel)) c.channel = 'Email';
  if (typeof c.owner !== 'string') c.owner = '';
}
function renderComms() {
  const host = document.getElementById('commList');
  const empty = document.getElementById('commEmpty');
  if (!host) return;
  const list = _commsRO();
  host.innerHTML = '';
  if (empty) empty.style.display = list.length ? 'none' : '';
  list.forEach(c => host.appendChild(_buildCommCard(c)));
  const cEl = document.getElementById('commsCount');
  if (cEl) cEl.textContent = list.length ? String(list.length) : '';
}
function _buildCommCard(c) {
  const card = document.createElement('div');
  card.className = 'assumption-card comm-card';
  card.dataset.id = c.id;
  const top = document.createElement('div');
  top.className = 'as-top';
  top.innerHTML =
    `<span class="as-regid" title="Communication id">${esc(_commRegId(c))}</span>` +
    `<span class="as-spacer"></span>` +
    `<label class="as-field">Frequency <select class="comm-freq">${COMM_FREQUENCIES.map(f => `<option value="${esc(f)}"${f === c.frequency ? ' selected' : ''}>${esc(f)}</option>`).join('')}</select></label>` +
    `<label class="as-field">Channel <select class="comm-chan">${COMM_CHANNELS.map(ch => `<option value="${esc(ch)}"${ch === c.channel ? ' selected' : ''}>${esc(ch)}</option>`).join('')}</select></label>` +
    `<span class="rc-card-actions"><button class="rc-delete" title="Delete communication">×</button></span>`;
  top.querySelector('.comm-freq').addEventListener('change', (e) => { if (COMM_FREQUENCIES.includes(e.target.value)) { c.frequency = e.target.value; markDirty(); } });
  top.querySelector('.comm-chan').addEventListener('change', (e) => { if (COMM_CHANNELS.includes(e.target.value)) { c.channel = e.target.value; markDirty(); } });
  top.querySelector('.rc-delete').addEventListener('click', () => deleteCommunication(c.id));
  card.appendChild(top);
  const what = document.createElement('div');
  what.className = 'rc-statement';
  what.contentEditable = 'true'; what.spellcheck = false;
  what.textContent = c.what || '';
  what.setAttribute('data-placeholder', 'What is communicated…');
  _onBlurCommit(what, () => { const v = (what.textContent || '').trim(); if (v !== (c.what || '')) { c.what = v; markDirty(); } });
  card.appendChild(what);
  card.appendChild(_buildCommAudienceRow(c));
  const meta = document.createElement('div');
  meta.className = 'rc-meta';
  const ownerField = document.createElement('span'); ownerField.className = 'rc-meta-field';
  ownerField.innerHTML = `Owner: <input type="text" list="stakeholderList" placeholder="(none)" value="${esc(c.owner || '')}">`;
  ownerField.querySelector('input').addEventListener('change', (e) => { const v = (e.target.value || '').trim(); if (v !== (c.owner || '')) { c.owner = v; markDirty(); } });
  meta.appendChild(ownerField);
  card.appendChild(meta);
  return card;
}
function _buildCommAudienceRow(c) {
  const row = document.createElement('div');
  row.className = 'comm-aud-row';
  const lbl = document.createElement('span');
  lbl.className = 'comm-aud-lbl';
  lbl.textContent = 'Audience:';
  row.appendChild(lbl);
  const mkChip = (text, onRemove) => {
    const chip = document.createElement('span');
    chip.className = 'comm-aud';
    chip.appendChild(document.createTextNode(text));
    const x = document.createElement('button');
    x.type = 'button'; x.title = 'Remove from audience'; x.textContent = '×';
    x.addEventListener('click', onRemove);
    chip.appendChild(x);
    return chip;
  };
  if (c.audienceAll) {
    row.appendChild(mkChip('All stakeholders', () => { c.audienceAll = false; markDirty(); renderComms(); }));
  } else {
    (c.audienceIds || []).forEach(id => {
      const s = STAKEHOLDERS.find(x => x.id === id);
      if (!s) return;   /* deleted stakeholder → chip silently drops off (Q-a) */
      row.appendChild(mkChip(s.name || '(unnamed)', () => {
        c.audienceIds = c.audienceIds.filter(x => x !== id);
        markDirty(); renderComms();
      }));
    });
  }
  const addable = c.audienceAll ? [] : STAKEHOLDERS.filter(s => !(c.audienceIds || []).includes(s.id));
  if (!c.audienceAll || addable.length) {
    const sel = document.createElement('select');
    sel.className = 'comm-aud-add';
    sel.innerHTML = `<option value="">+ Add audience…</option>`
      + (c.audienceAll ? '' : `<option value="all">All stakeholders</option>`
        + addable.map(s => `<option value="${esc(String(s.id))}">${esc(s.name || '(unnamed)')}</option>`).join(''));
    sel.addEventListener('change', () => {
      const v = sel.value;
      if (!v) return;
      if (v === 'all') { c.audienceAll = true; c.audienceIds = []; }
      else { const id = Number(v); if (isFinite(id) && !(c.audienceIds || []).includes(id)) c.audienceIds.push(id); }
      markDirty(); renderComms();
    });
    row.appendChild(sel);
  }
  return row;
}
function addCommunication() {
  if (!Array.isArray(appMeta.communications)) appMeta.communications = [];   /* lazy — round-trip-safe */
  const c = { id: _nextCommSeq(), what: '', audienceAll: false, audienceIds: [], frequency: 'Ad hoc', channel: 'Email', owner: '' };
  _normComm(c);
  appMeta.communications.push(c);
  markDirty();
  renderComms();
  const card = document.querySelector(`#commList .comm-card[data-id="${c.id}"]`);
  if (card) { const w = card.querySelector('.rc-statement'); if (w) { card.scrollIntoView({ block: 'center' }); w.focus(); } }
}
function deleteCommunication(id) {
  const list = _commsRO();
  const idx = list.findIndex(c => c.id === id);
  if (idx < 0) return;
  const c = list[idx];
  const label = (c.what && c.what.trim()) ? '"' + _escapeHtml(c.what.trim()) + '"' : 'this communication';
  showConfirm('Delete communication?',
    'This will remove ' + label + ' (' + _escapeHtml(_commRegId(c)) + ') from the plan.<br><br>You\'ll have 6 seconds to undo.',
    () => {
      const arr = _commsRO();
      const i2 = arr.findIndex(x => x.id === id);
      if (i2 < 0) return;
      const removed = arr.splice(i2, 1)[0]; const removedIndex = i2;
      markDirty(); renderComms();
      showUndoToast('Communication deleted', () => {
        arr.splice(removedIndex, 0, removed);
        markDirty(); renderComms();
        showToast('Communication restored', { type: 'info' });
      });
    });
}

/* ══ v8.8: KPIs (Governance · third section) ═══════════════════════════════
   Five FIXED dimensions — Cost · Risk · Schedule · Scope · Quality (verbatim
   PMBOK knowledge-area terms; deliberately NO custom-KPI builder — Model A).
   Each renders as a definition card: a manual RAG pill (the ONLY editable copy —
   the Overview dots are a read-only echo), a one-line rationale, and four
   OPTIONAL free-text definition slots (measure · baseline → target · current ·
   Amber/Red thresholds — stated criteria, never computed). The per-card hint is
   the app's own read of live data (open High×High risks / overdue actions / CRs
   pending decision) — it informs the judgement, it never makes it. No mechanical
   roll-up anywhere: the Overview Status RAG stays the human synthesis.
   Data is LAZY (the crSeq pattern): appMeta.dashboard.kpis and each dimension
   record are created on the FIRST real edit only, so a KPI-less file serialises
   byte-identically (no data change for existing files; schema stays 7). Render
   paths read via _kpisRO()/_kpiRec() and NEVER write — the boot render precedes
   the round-trip check; keep it that way. */
const KPI_DIMS = [
  ['cost', 'Cost'], ['risk', 'Risk'], ['schedule', 'Schedule'],
  ['scope', 'Scope'], ['quality', 'Quality']
];
function _kpisRO() {
  const d = appMeta && appMeta.dashboard;
  return (d && d.kpis && typeof d.kpis === 'object') ? d.kpis : {};
}
function _kpiRec(dim) {
  const r = _kpisRO()[dim];
  return (r && typeof r === 'object') ? r : {};
}
function _kpiWrite(dim, field, value) {
  /* the ONLY writer — materialises the lazy storage on the first real edit */
  if (!KPI_DIMS.some(d => d[0] === dim)) return;
  const D = appMeta.dashboard;
  if (!D.kpis || typeof D.kpis !== 'object') D.kpis = {};
  if (!D.kpis[dim] || typeof D.kpis[dim] !== 'object') D.kpis[dim] = {};
  D.kpis[dim][field] = value;
  markDirty();
}
function _kpiLevel(dim) {
  const v = _kpiRec(dim).rag;
  return ['red', 'amber', 'green'].includes(v) ? v : null;
}
function cycleKpiRag(dim) {
  let i = _RAG_CYCLE.indexOf(_kpiLevel(dim));
  if (i < 0) i = 0;
  _kpiWrite(dim, 'rag', _RAG_CYCLE[(i + 1) % _RAG_CYCLE.length]);
  renderKpis(); renderKpiDots();
}
/* Derived hints — display-only counts from data the app already holds. Cost and
   Quality have no in-app source, so they get none. Overdue matches the milestone
   list's convention (merged edits; Completed/Cancelled excluded; due < today). */
function _kpiHint(dim) {
  if (dim === 'risk') {
    /* v13.1 (A18): THREATS only. "High × High" is the top-right corner of a threat matrix —
       for an opportunity, high probability × high impact is the BEST case, not a warning —
       so counting them here inflated the Risk KPI's hint with the very things you want.
       phase === 'risk' already excludes promoted issues; type is the half that was missing. */
    const n = RISKS.filter(r => r && r.phase === 'risk' && r.status === 'Open'
      && r.type === 'threat'
      && r.probability === 'high' && r.impact === 'high').length;
    return n + ' High × High risk' + (n === 1 ? '' : 's') + ' open';
  }
  if (dim === 'schedule') {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const n = getAllActions().filter(a => {
      const e = edits[a.id] || {};
      const status = e.status || STATUS_DISPLAY[a.status] || a.status;
      if (status === 'Completed' || status === 'Cancelled') return false;
      /* ⚠⚠ v16.8 (W4): THE SECOND SITE, AND SWEEP-2 MEASURED ONLY THE FIRST. This comment
         block says "Overdue matches the milestone list's convention" — it did, including the
         defect, because both wrote `e.due || a.due`. Counted before asserting: that expression
         appeared exactly twice in the shell, and a fix to one of them would have left the
         Governance KPI hint quietly disagreeing with the Overview it claims to match. */
      const due = _parseDueDate(actField(e, a, 'due'));
      return due && due < today;
    }).length;
    return n + ' action' + (n === 1 ? '' : 's') + ' overdue';
  }
  if (dim === 'scope') {
    const n = (appMeta.changeRequests || []).filter(c =>
      c && (c.status === 'submitted' || c.status === 'review')).length;
    return n + ' CR' + (n === 1 ? '' : 's') + ' pending decision';
  }
  return null;
}
function renderKpis() {
  const host = document.getElementById('kpiList');
  if (!host) return;
  host.innerHTML = '';
  KPI_DIMS.forEach(d => host.appendChild(_buildKpiCard(d[0], d[1])));
  const cEl = document.getElementById('kpiCount');
  if (cEl) cEl.textContent = String(KPI_DIMS.length);
}
function _buildKpiCard(dim, label) {
  const k = _kpiRec(dim);                     /* read-only view — may be {} */
  const card = document.createElement('div');
  card.className = 'kpi-card';
  card.dataset.dim = dim;
  const head = document.createElement('div');
  head.className = 'kpi-head';
  const lbl = document.createElement('span');
  lbl.className = 'kpi-label'; lbl.textContent = label;
  const lvl = _kpiLevel(dim);
  const pill = document.createElement('span');
  pill.className = 'rag-pill kpi-pill ' + _ragClassFor(lvl);
  pill.textContent = (lvl === null) ? 'Not set' : _RAG_LABEL[lvl];
  pill.title = 'Judged against the stated criteria — click to cycle';
  pill.addEventListener('click', () => cycleKpiRag(dim));
  head.appendChild(lbl); head.appendChild(pill);
  card.appendChild(head);
  const def = document.createElement('div');
  def.className = 'kpi-def';
  const mkField = (field, ph, extraCls) => {
    const el = document.createElement('span');
    el.className = 'kpi-field' + (extraCls ? ' ' + extraCls : '');
    el.contentEditable = 'true'; el.spellcheck = false;
    el.setAttribute('data-ph', ph);
    el.textContent = (typeof k[field] === 'string') ? k[field] : '';
    _onBlurCommit(el, () => {
      const v = (el.textContent || '').trim();
      const cur = (typeof _kpiRec(dim)[field] === 'string') ? _kpiRec(dim)[field] : '';
      if (v !== cur) _kpiWrite(dim, field, v);   /* a no-op blur never creates storage */
    });
    return el;
  };
  const addRow = (keyLabel, nodes) => {
    const kEl = document.createElement('span'); kEl.className = 'kpi-k'; kEl.textContent = keyLabel;
    const vEl = document.createElement('span'); vEl.className = 'kpi-v';
    nodes.forEach(nd => vEl.appendChild(nd));
    def.appendChild(kEl); def.appendChild(vEl);
  };
  addRow('Measure', [mkField('measure', 'what is measured, and its unit…')]);
  const arrow = document.createElement('span'); arrow.className = 'kpi-bt-arrow'; arrow.textContent = '→';
  const nowSep = document.createElement('span'); nowSep.className = 'kpi-now'; nowSep.textContent = '· now ';
  addRow('Base → Target', [mkField('baseline', 'baseline…'), arrow, mkField('target', 'target…'), nowSep, mkField('current', 'current…', 'kpi-now')]);
  addRow('Thresholds', [mkField('thresholds', 'what Amber / Red would take…')]);
  card.appendChild(def);
  const rat = document.createElement('div');
  rat.className = 'kpi-rationale';
  rat.contentEditable = 'true'; rat.spellcheck = false;
  rat.textContent = (typeof k.rationale === 'string') ? k.rationale : '';
  _onBlurCommit(rat, () => {
    const v = (rat.textContent || '').trim();
    const cur = (typeof _kpiRec(dim).rationale === 'string') ? _kpiRec(dim).rationale : '';
    if (v !== cur) _kpiWrite(dim, 'rationale', v);
  });
  card.appendChild(rat);
  const hint = _kpiHint(dim);
  if (hint !== null) {
    const h = document.createElement('div');
    h.className = 'kpi-hint';
    h.textContent = 'hint: ' + hint;
    card.appendChild(h);
  }
  return card;
}
/* ── Overview echo: five read-only dots + click-through. Display-only by hard
   design constraint — only Governance's pills are ever editable, so the same
   judgement is never held in two writable places (the views cannot disagree). */
function renderKpiDots() {
  const row = document.getElementById('kpiDotRow');
  if (!row) return;
  row.innerHTML = '';
  KPI_DIMS.forEach(d => {
    const dim = d[0], label = d[1];
    const lvl = _kpiLevel(dim);
    const item = document.createElement('span');
    item.className = 'kpi-dot-item';
    item.title = label + ' KPI — view and edit in Governance ▸ KPIs';
    const dot = document.createElement('span');
    dot.className = 'kpi-dot' + (lvl ? ' kd-' + lvl : '');
    const word = document.createElement('span');
    word.className = 'kpi-dot-word'; word.textContent = label;
    item.appendChild(dot); item.appendChild(word);
    item.addEventListener('click', goToKpis);
    row.appendChild(item);
  });
  const link = document.createElement('button');
  link.type = 'button';
  link.className = 'kpi-dot-link';
  link.textContent = '↳ Governance ▸ KPIs';
  link.title = 'Open the editable KPIs (these dots are display-only)';
  link.addEventListener('click', goToKpis);
  row.appendChild(link);
}
function goToKpis() {
  switchTab('changes');
  const s = document.getElementById('kpiSection');
  if (s) { s.open = true; s.scrollIntoView({ block: 'start', behavior: 'smooth' }); }
}

/* ── Add-risk modal ──────────────────────────────────────────────────────── */
function syncRiskModalStrategies() {
  const type = document.getElementById('r-type').value;
  const sel = document.getElementById('r-strategy');
  if (!sel) return;
  const strategies = (type === 'threat') ? RISK_THREAT_STRATEGIES : RISK_OPPORTUNITY_STRATEGIES;
  sel.innerHTML = strategies.map(s => `<option value="${esc(s)}">${esc(s)}</option>`).join('');
}
function openAddRiskModal() {
  /* Reset fields to defaults */
  document.getElementById('r-type').value = 'threat';
  document.getElementById('r-statement').value = '';
  document.getElementById('r-category').value = 'Technical';
  document.getElementById('r-probability').value = 'medium';
  document.getElementById('r-impact').value = 'medium';
  document.getElementById('r-status').value = 'Open';
  document.getElementById('r-owner').value = '';
  document.getElementById('r-notes').value = '';
  syncRiskModalStrategies();
  document.getElementById('riskModalOverlay').classList.add('open');
  setTimeout(() => document.getElementById('r-statement').focus(), 20);
}
function closeAddRiskModal() {
  document.getElementById('riskModalOverlay').classList.remove('open');
}
function handleRiskModalOverlayClick(e) {
  if (e.target.id === 'riskModalOverlay') closeAddRiskModal();
}
function submitNewRisk() {
  const type = document.getElementById('r-type').value;
  const statement = document.getElementById('r-statement').value.trim();
  if (!statement) { alert('Please enter a risk statement.'); return; }
  const r = {
    id: _nextRiskSeq(),
    type: RISK_VALID_TYPES.includes(type) ? type : 'threat',
    statement,
    category: document.getElementById('r-category').value,
    probability: document.getElementById('r-probability').value,
    impact: document.getElementById('r-impact').value,
    strategy: document.getElementById('r-strategy').value,
    status: document.getElementById('r-status').value,
    owner: (document.getElementById('r-owner').value || '').trim(),
    notes: (document.getElementById('r-notes').value || '').trim(),
    direct: false,
  };
  _normRisk(r);   /* derives phase from type+status — a threat logged as Occurred promotes straight to an issue */
  RISKS.push(r);
  markDirty();
  closeAddRiskModal();
  _afterRiskMutation();
}
/* v8.4: log a problem straight in the issue phase (it was never a risk → no History line).
   Creates a blank issue card and focuses it for inline editing (mirrors addWorkstream). */
function logDirectIssue() {
  const r = {
    id: _nextRiskSeq(), type: 'threat', statement: '',
    category: 'Technical', probability: 'medium', impact: 'medium', strategy: 'Accept',
    status: 'Occurred', owner: '', notes: '', direct: true,
    phase: 'issue', raised: _todayISO(), severity: 'Medium', workStatus: 'Open', resolution: '',
  };
  RISKS.push(r);
  markDirty();
  _afterRiskMutation();
  const card = document.querySelector(`#riskList .risk-card[data-id="${r.id}"]`);
  if (card) { const s = card.querySelector('.rc-statement'); if (s) { card.scrollIntoView({ block: 'center' }); s.focus(); } }
}

/* ── Matrix ──────────────────────────────────────────────────────────────── */
let _riskMatrixMode = 'threat';
function setRiskMatrixMode(m) {
  if (m !== 'threat' && m !== 'opportunity') return;
  _riskMatrixMode = m;
  const tBtn = document.getElementById('riskMatrixThreat');
  const oBtn = document.getElementById('riskMatrixOpp');
  _setSelected(tBtn, m === 'threat');   /* v13.1 (A58) */
  _setSelected(oBtn, m === 'opportunity');
  renderRiskMatrix();
}
function renderRiskMatrix() {
  const layout = document.getElementById('riskMatrixLayout');
  if (!layout) return;
  layout.innerHTML = '';
  const impacts = ['high', 'medium', 'low'];   /* rows top→bottom */
  const probs   = ['low', 'medium', 'high'];   /* cols left→right */
  const mode = _riskMatrixMode;

  const ytitle = document.createElement('div');
  ytitle.className = 'rm-ytitle'; ytitle.textContent = 'Impact';
  layout.appendChild(ytitle);

  impacts.forEach((imp) => {
    const ylab = document.createElement('div');
    ylab.className = 'rm-ylabel'; ylab.textContent = _RISK_CAP(imp);
    layout.appendChild(ylab);
    probs.forEach((prob) => {
      /* v13.1 (A37): the cell's own score through the one definition, not a fourth
         inline multiplication of the same two ranks. */
      const s = _riskScore({ probability: prob, impact: imp });
      const cell = document.createElement('div');
      const colorCls = (mode === 'threat') ? _SEV_CLASS[_riskBand(s)] : _OPP_CLASS[_riskBand(s)];
      cell.className = 'rm-cell ' + colorCls;
      RISKS.filter(r => r.type === mode && r.phase !== 'issue' && r.status !== 'Closed' && r.probability === prob && r.impact === imp)
        .forEach(r => {
          const dot = document.createElement('span');
          dot.className = 'rm-dot ' + r.type;
          dot.textContent = _registerId(r);   /* v15.2: derived ref, matching the register beneath the matrix */
          dot.title = r.statement;
          cell.appendChild(dot);
        });
      const sc = document.createElement('span');
      sc.className = 'rm-cellscore'; sc.textContent = s;
      cell.appendChild(sc);
      layout.appendChild(cell);
    });
  });

  /* Bottom-left empty corners + x labels + x title */
  layout.appendChild(document.createElement('div'));
  layout.appendChild(document.createElement('div'));
  probs.forEach(prob => {
    const xl = document.createElement('div');
    xl.className = 'rm-xlabel'; xl.textContent = _RISK_CAP(prob);
    layout.appendChild(xl);
  });
  layout.appendChild(document.createElement('div'));
  layout.appendChild(document.createElement('div'));
  const xtitle = document.createElement('div');
  xtitle.className = 'rm-xtitle'; xtitle.textContent = 'Probability →';
  layout.appendChild(xtitle);

  /* Legend */
  const legend = document.getElementById('riskMatrixLegend');
  if (!legend) return;
  if (mode === 'threat') {
    legend.innerHTML =
      '<div class="rml-item"><span class="rml-sw" style="background:var(--low-bg);border-color:var(--low-border)"></span>Low (1–2)</div>' +
      '<div class="rml-item"><span class="rml-sw" style="background:var(--medium-bg);border-color:var(--medium-border)"></span>Medium (3–4)</div>' +
      '<div class="rml-item"><span class="rml-sw" style="background:var(--high-bg);border-color:var(--high-border)"></span>High (6–9)</div>';
  } else {
    legend.innerHTML =
      '<div class="rml-item"><span class="rml-sw" style="background:var(--surface2);border-color:var(--border)"></span>Low potential</div>' +
      '<div class="rml-item"><span class="rml-sw" style="background:var(--opp-bg);border-color:var(--opp-border)"></span>Moderate</div>' +
      '<div class="rml-item"><span class="rml-sw" style="background:var(--opp);border-color:var(--opp)"></span>High potential</div>';
  }
}

/* ── Dashboard live-refresh hook ─────────────────────────────────────────── */
/* v3.3: when risks change, the Dashboard "Key risks" section re-renders.
   Mirrors _refreshDashboardFromActions() from v3.2. Safe to call any time. */
function _refreshDashboardFromRisks() {
  try {
    if (typeof renderKeyRisks === 'function') renderKeyRisks();
  } catch (e) { /* never let a dashboard render break a risk mutation */ }
}


function _initDashboardV32() {
  renderRag();
  const rat = document.getElementById('ragRationale');
  if (rat) {
    _onBlurCommit(rat, () => {
      const v = (rat.textContent || '').trim();
      if (v !== (appMeta.dashboard.rag.rationale || '')) { appMeta.dashboard.rag.rationale = v; markDirty(); }
    });
  }
  renderScope();
  renderGates();
  renderMilestones();
  renderKeyRisks();
  renderWorkstreams();
  /* Picker search filter */
  const search = document.getElementById('spPickerSearch');
  if (search) search.addEventListener('input', () => _renderPickerList(search.value));
  /* Close picker on overlay click */
  const ov = document.getElementById('spPickerOverlay');
  if (ov) ov.addEventListener('click', (e) => { if (e.target === ov) closeActionPicker(); });
}

/* HTML-escape helper for the innerHTML-built rows above (defensive — action
   descriptions and project titles are user text). */
/* v8.2.3 (Q8): canonical HTML escaper — esc() (defined earlier) delegates here. */
function _escapeHtml(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}
/* ── ⚠ v13.1 (A56): a value going into a JS STRING that sits inside an HTML ATTRIBUTE ────
   onclick="gotoProject('HERE')" is two nested languages, and it needs two escapes applied
   in the right ORDER. _escapeHtml alone is not enough, and — the part worth knowing — the
   obvious repair does not work either: the portfolio row already did
   `_escapeHtml(P.id).replace(/'/g, "\\'")`, which is DEAD CODE. _escapeHtml has already
   turned every apostrophe into &#39;, so there is nothing left for the replace to find; the
   HTML parser then decodes &#39; straight back into an apostrophe BEFORE the JS parser sees
   it, and the string is broken open exactly as if nothing had been done. A mitigation that
   cannot fire is worse than none, because it stops anyone looking again.
   So: escape for JS FIRST (backslashes before quotes, or the escapes escape each other),
   and let _escapeHtml run over the result — `\'` survives as `\&#39;`, which decodes to `\'`
   and reaches the JS parser as a properly escaped quote.
   ⚠ For a PLAIN attribute (data-id, id, title) keep using _escapeHtml: this function would
   put a literal backslash into the value. Two contexts, two helpers, deliberately. */
function _jsInAttr(v) {
  return _escapeHtml(String(v == null ? '' : v).replace(/\\/g, '\\\\').replace(/'/g, "\\'"));
}


const _CHARTER_OUTLINE = 'Purpose and intended outcomes:\n\nHigh-level scope:\n\nSponsor, project manager and key stakeholders:\n\nSuccess measures and key commitments:\n\n';
let _charterBoundProjectUid = null;
let _charterBoundProject = null;
let _charterLastValue = '';
function _charterActiveProject() {
  const db = window.__DB__;
  return db && Array.isArray(db.projects)
    ? db.projects.find(c => c && c.id === activeProjectId) : null;
}
function _charterCanInsert(project, ta) {
  return !!(project && ta && !ta.disabled && !ta.readOnly
    && typeof project.uid === 'string' && project.uid === _charterBoundProjectUid);
}
function _commitDashboardNotes() {
  const ta = document.getElementById('dashNotes');
  const project = _charterActiveProject();
  if (!_charterCanInsert(project, ta) || !appMeta.dashboard) return false;
  const next = ta.value || '';
  const current = typeof appMeta.dashboard.notes === 'string' ? appMeta.dashboard.notes : '';
  ta.textContent = next;
  if (next === current) return false;
  appMeta.dashboard.notes = next;
  markDirty();
  return true;
}
function _renderCharterValueState(resetGuide) {
  const ta = document.getElementById('dashNotes');
  const guide = document.getElementById('charterGuide');
  const prompt = document.getElementById('charterEmptyPrompt');
  const button = document.getElementById('charterOutlineBtn');
  if (!ta || !guide || !prompt || !button) return;
  const empty = ta.value === '';
  const project = _charterActiveProject();
  guide.hidden = !project;
  guide.inert = !project;
  prompt.hidden = !project || !empty;
  button.hidden = !empty || !_charterCanInsert(project, ta);
  if (resetGuide) guide.open = !!project && empty;
  _charterLastValue = ta.value;
}
function _bindCharterGuide(project) {
  const uid = project && typeof project.uid === 'string' ? project.uid : null;
  const changed = uid !== _charterBoundProjectUid || project !== _charterBoundProject;
  _charterBoundProjectUid = uid;
  _charterBoundProject = project || null;
  _renderCharterValueState(changed || uid === null);
}
function insertCharterOutline() {
  const ta = document.getElementById('dashNotes');
  const project = _charterActiveProject();
  if (!_charterCanInsert(project, ta) || ta.value !== '') return false;
  ta.value = _CHARTER_OUTLINE;
  ta.textContent = ta.value;
  _commitDashboardNotes();
  const guide = document.getElementById('charterGuide');
  if (guide) guide.open = false;
  _renderCharterValueState(false);
  ta.focus();
  const caret = _CHARTER_OUTLINE.indexOf('\n') + 1;
  ta.setSelectionRange(caret, caret);
  return true;
}
function navigateFromCharter(tab, sectionId) {
  _commitDashboardNotes();
  switchTab(tab);
  const section = document.getElementById(sectionId);
  if (!section) return;
  section.open = true;
  const summary = section.querySelector(':scope > summary');
  requestAnimationFrame(() => requestAnimationFrame(() => {
    const reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    section.scrollIntoView({ block: 'center', behavior: reduced ? 'auto' : 'smooth' });
    if (summary) summary.focus({ preventScroll: true });
  }));
}
(function initDashboardNotes() {
  const ta = document.getElementById('dashNotes');
  if (!ta) return;
  ta.value = (appMeta.dashboard && typeof appMeta.dashboard.notes === 'string') ? appMeta.dashboard.notes : '';
  _charterLastValue = ta.value;
  _onBlurCommit(ta, _commitDashboardNotes);
  /* Mirror the v2.99 textarea-export pattern: keep innerHTML in sync with .value
     so document.outerHTML serializes the live state on save. */
  ta.addEventListener('input', () => {
    const wasEmpty = _charterLastValue === '';
    ta.textContent = ta.value;
    const isEmpty = ta.value === '';
    const guide = document.getElementById('charterGuide');
    if (guide && wasEmpty !== isEmpty) guide.open = isEmpty;
    _renderCharterValueState(false);
  });
})();
/* v2.15: apply saved expanded state to action cards now that they're rendered */
applyExpandedActionsFromState();
// v2.4: also render sessions on page-load init. Defense-in-depth: even if a future
// regression breaks the outerHTML serialization fix in exportFile(), reopening a
// saved file will still display correct content because we rebuild textareas from
// the (always-correct) SESSIONS data, regardless of stale DOM state.
renderSessions();
/* v4.0: initialise the live meeting indicator now that ACTIONS/edits + the header
   DOM are ready (restores from appMeta.meetingTimer; resumes ticking if running). */
initMeetingIndicator();
/* v2.11: now that sessions are rendered, apply persisted expanded states */
applyExpandedSessionsFromState();
// v2.9: sync expand-toggle button labels with the rendered DOM state at load
syncExpandToggleLabels();
syncDuplicateBtnState();
/* v2.90: init complete — from now on markDirty() may fire normally on user actions.
   Also clear any .dirty class that may have been applied (defensive — e.g. if Chrome
   fired a contenteditable blur during init despite the bootCompleted gate). */
bootCompleted = true;
isDirty = false;
{
  const _btn = document.getElementById('saveBtn');
  if (_btn) _btn.classList.remove('dirty');
}
/* v3.2: initialise all dashboard sections now — deferred to the end of the
   script so the v3.2 module's const declarations (_RAG_CYCLE/_RAG_LABEL, etc.)
   are past their TDZ before renderRag() runs. */
_initDashboardV32();
/* v3.3: initialise the risk register tab (cards + matrix) — same deferred
   placement as the v3.2 dashboard init so all const deps are past TDZ. */
if (typeof renderRisks === 'function') renderRisks();

